import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ExpenseGrid = ({ expenses, onEdit, onDelete, onApprove }) => {
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedExpenses(expenses?.map(exp => exp?.id));
    } else {
      setSelectedExpenses([]);
    }
  };

  const handleSelectExpense = (id) => {
    setSelectedExpenses(prev =>
      prev?.includes(id) ? prev?.filter(expId => expId !== id) : [...prev, id]
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendente', color: 'bg-warning/10 text-warning border-warning', icon: 'Clock' },
      approved: { label: 'Aprovado', color: 'bg-success/10 text-success border-success', icon: 'CheckCircle' },
      rejected: { label: 'Rejeitado', color: 'bg-error/10 text-error border-error', icon: 'XCircle' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  const sortedExpenses = [...expenses]?.sort((a, b) => {
    if (sortConfig?.key === 'date') {
      return sortConfig?.direction === 'asc'
        ? new Date(a.date) - new Date(b.date)
        : new Date(b.date) - new Date(a.date);
    }
    if (sortConfig?.key === 'amount') {
      return sortConfig?.direction === 'asc' ? a?.amount - b?.amount : b?.amount - a?.amount;
    }
    return 0;
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedExpenses?.length / ITEMS_PER_PAGE);
  const paginatedExpenses = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sortedExpenses?.slice(startIndex, endIndex);
  }, [sortedExpenses, currentPage]);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages?.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages?.push(i);
        }
        pages?.push('...');
        pages?.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages?.push(1);
        pages?.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages?.push(i);
        }
      } else {
        pages?.push(1);
        pages?.push('...');
        pages?.push(currentPage - 1);
        pages?.push(currentPage);
        pages?.push(currentPage + 1);
        pages?.push('...');
        pages?.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="h-full flex flex-col bg-card">
      {selectedExpenses?.length > 0 && (
        <div className="p-4 bg-primary/10 border-b border-primary flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">
            {selectedExpenses?.length} despesa(s) selecionada(s)
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" iconName="Check">
              Aprovar em Lote
            </Button>
            <Button variant="outline" size="sm" iconName="Tag">
              Categorizar
            </Button>
            <Button variant="outline" size="sm" iconName="Download">
              Exportar
            </Button>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th className="p-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedExpenses?.length === expenses?.length}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th
                className="p-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center gap-2">
                  Data de Pagamento
                  <Icon name={sortConfig?.key === 'date' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={16} />
                </div>
              </th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Fornecedor</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Categoria</th>
              <th
                className="p-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('amount')}
              >
                <div className="flex items-center gap-2">
                  Valor
                  <Icon name={sortConfig?.key === 'amount' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={16} />
                </div>
              </th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Cliente</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Status</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Comprovante</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Ações</th>
            </tr>
          </thead>
          <tbody>
            {paginatedExpenses?.map((expense) => (
              <tr
                key={expense?.id}
                className="border-b border-border hover:bg-muted/50 transition-colors"
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedExpenses?.includes(expense?.id)}
                    onChange={() => handleSelectExpense(expense?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-3 text-sm text-foreground">
                  {expense?.date ? new Date(expense.date + 'T00:00:00')?.toLocaleDateString('pt-BR') : '-'}
                </td>
                <td className="p-3">
                  <div className="text-sm font-medium text-foreground">{expense?.vendor}</div>
                  <div className="text-xs text-muted-foreground">{expense?.description}</div>
                </td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-muted text-foreground">
                    <Icon name={expense?.categoryIcon} size={12} />
                    {expense?.category}
                  </span>
                </td>
                <td className="p-3 text-sm font-semibold text-foreground">
                  R$ {expense?.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </td>
                <td className="p-3 text-sm text-muted-foreground">
                  {expense?.client || '-'}
                </td>
                <td className="p-3">{getStatusBadge(expense?.status)}</td>
                <td className="p-3">
                  {expense?.receipt ? (
                    <button className="relative group">
                      <Image
                        src={expense?.receipt}
                        alt={expense?.receiptAlt}
                        className="w-12 h-12 rounded object-cover border border-border"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                        <Icon name="Eye" size={16} color="#FFFFFF" />
                      </div>
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">Sem comprovante</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    {expense?.status === 'pending' && (
                      <button
                        onClick={() => onApprove(expense?.id)}
                        className="p-1.5 rounded hover:bg-success/10 text-success transition-colors"
                        title="Aprovar"
                      >
                        <Icon name="Check" size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(expense?.id)}
                      className="p-1.5 rounded hover:bg-muted text-foreground transition-colors"
                      title="Editar"
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      onClick={() => onDelete(expense?.id)}
                      className="p-1.5 rounded hover:bg-error/10 text-error transition-colors"
                      title="Excluir"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-border flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, expenses?.length)} de {expenses?.length} despesa(s)
        </span>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            iconName="ChevronLeft"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            Anterior
          </Button>
          
          {getPageNumbers()?.map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">...</span>
            ) : (
              <button
                key={page}
                onClick={() => handlePageClick(page)}
                className={`px-3 py-1 rounded ${
                  currentPage === page 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted text-foreground'
                }`}
              >
                {page}
              </button>
            )
          ))}
          
          <Button 
            variant="outline" 
            size="sm" 
            iconName="ChevronRight"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Próximo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseGrid;